import { SelectionModel } from '@angular/cdk/collections';
import { computed, EventEmitter, inject, Injectable, Signal, signal } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  BulkActionsProps,
  CanvasElement,
  CanvasElementEvent,
  CanvasElementId,
  CanvasElementType,
  DataInputFacade,
  PropsOf,
  RowActionsProps,
  RuntimeContainerActionModel,
} from '@blaze-runtime/engine-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { UserAccessRolesService } from '@blaze-shared/auth-data-access';
import { levelParentOfItem } from '@blaze-shared/datasets-domain';
import {
  ActionsDisplayMode,
  BULK_ACTION_AGGREGATED_IDS,
  DataBindingSourceInfo,
  getActiveBinding,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputTableColumnModel,
  RuntimeContext,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type ElementSchema, isGroupByRow } from '@blaze-shared/schema-domain';
import { countedShareReplay, isDeepEqual, isNullishOrEmptyString } from '@blaze-shared/shared-util';
import { isPrintToPdfNode } from '@blaze-shared/workflow-domain';

import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import {
  ContainerActionsService,
  ListLikeActionResult,
} from '../../services/container-actions.service';
import { evaluateExpression } from '../../utils/elements/expressions.utils';

export interface PropertiesConfigInputTableColumnModelForGrouping
  extends PropertiesConfigInputTableColumnModel {
  originName: string;
}

export interface OnContainerActionParams {
  action: RuntimeContainerActionModel;
  event?: Event;
  dataRow?: any;
  isBulkAction?: boolean;
  menu?: MatMenu;
  actionId?: string;
}

export interface OnContainerActionParamsWithExtras extends OnContainerActionParams {
  output: EventEmitter<CanvasElementEvent>;
  dataRowSchema: ElementSchema | undefined;
}

const DISABLE_ACTION_WHEN_PENDING_TIME = 300;

@Injectable()
export class ElementDataActions<TElementType extends CanvasElementType> {
  private readonly containerActionsService = inject(ContainerActionsService);
  private readonly intellisense = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly userAccessRolesService = inject(UserAccessRolesService);

  handlesFocus = false;
  outputEmitAllowed = true;
  groupColsDefs: PropertiesConfigInputTableColumnModelForGrouping[] = [];

  singleSelect = signal(true);
  selection: SelectionModel<unknown>; // selection checkboxes state of the rows
  focus: SelectionModel<unknown>; // highlight state of the rows

  visibleRowActions$: Observable<RuntimeContainerActionModel[][]>;
  visibleBulkActions$: Observable<RuntimeContainerActionModel[]>;
  visibleOtherActions$: Observable<RuntimeContainerActionModel[]>;

  visibleHeaderActions$: Observable<RuntimeContainerActionModel[]>;

  bulkAllSelected = false;
  bulkActions: PropertiesConfigInputContainerItemActionModel[] = [];
  bulkActionsHaveIcons = false;
  headerActionsHaveIcons = false;
  bulkActionsDisplayMode: ActionsDisplayMode = 'always';
  bulkActionsGrouped = false;

  elementId: Signal<CanvasElementId | undefined>;
  elementType: CanvasElementType;
  props: Signal<PropsOf<TElementType> | undefined>;
  dataValue: DataInputFacade<'value'>;

  pendingActions = signal<{ [key: string]: number }>({});
  idColumnName = computed(() => {
    const dataSource = this.propsDataSource();
    if (dataSource) {
      return this.schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType);
    }
    return undefined;
  });

  disableActionWhenPending = computed((): boolean => {
    const props = this.props() as any;
    return (
      !!props?.content &&
      'actions' in props.content &&
      'disableActionWhenPending' in props.content.actions &&
      !!props.content.actions.disableActionWhenPending
    );
  });

  propsDataSource = computed((): DataBindingSourceInfo | undefined => {
    const props = this.props();
    if (!arePropsWithData(props)) {
      return undefined;
    }
    return getActiveBinding(props.data, 'value')?.dataSource;
  });
  get focusCount(): number {
    return this.focus.selected.filter((data) => !isGroupByRow(data)).length;
  }

  get selectionCount(): number {
    return this.selection.selected.length;
  }

  constructor() {
    this.selection = new SelectionModel<unknown>(!this.singleSelect(), []);
    this.focus = new SelectionModel<unknown>(!this.singleSelect(), []);
  }

  updateSelectionMode(hasBulkActions: boolean): void {
    this.singleSelect.set(!hasBulkActions);
  }

  resetSelectionModel(): void {
    this.selection = new SelectionModel<unknown>(
      !this.singleSelect(),
      this.selectionCount
        ? this.singleSelect()
          ? [this.selection.selected[0]]
          : this.selection.selected
        : [],
      undefined,
      isDeepEqual,
    );
    this.focus = new SelectionModel<unknown>(
      !this.singleSelect(),
      this.focusCount ? (this.singleSelect() ? [this.focus.selected[0]] : this.focus.selected) : [],
      undefined,
      isDeepEqual,
    );
  }

  toggleSelection(
    item: unknown,
    value: any[],
    params?: { allowBulkOutputEmit?: boolean; select?: boolean },
  ): void {
    if (!params) {
      params = {};
    }
    this.bulkAllSelected = false;
    if (params.select) {
      this.selection.toggle(item);
      if (
        this.selection.isSelected(item) &&
        this.focus.selected.length === 1 &&
        !this.selection.isSelected(this.focus.selected[0])
      ) {
        this.selection.select(this.focus.selected[0]);
      }
    }
    if (this.handlesFocus) {
      this.focus.toggle(item);
    }

    if (isGroupByRow(item)) {
      const isSelected = this.selection.isSelected(item);
      for (let i = value.findIndex((val) => val.__id === item.__id) + 1; i < value.length; i++) {
        const element = value[i];
        if (isGroupByRow(element) && element.__level <= item.__level) {
          break;
        }
        if (isSelected) {
          if (params.select) {
            this.selection.select(element);
          }
          if (this.handlesFocus) {
            this.focus.select(element);
          }
        } else {
          if (params.select) {
            this.selection.deselect(element);
          }
          if (this.handlesFocus) {
            this.focus.deselect(element);
          }
        }
      }
    }
    const clickedItem = item as Record<string, string>;
    if (this.bulkActions.length && !params.allowBulkOutputEmit) {
      this.outputEmitAllowed = false;
    }
    if (this.groupColsDefs.length) {
      let parentLevelItem = levelParentOfItem(value, clickedItem);
      while (parentLevelItem) {
        if (this.selection.isSelected(parentLevelItem) !== this.selection.isSelected(item)) {
          if (this.selection.isSelected(parentLevelItem) && params.select) {
            this.selection.deselect(parentLevelItem);
          }
          if (this.focus.isSelected(parentLevelItem) && this.handlesFocus) {
            this.focus.deselect(parentLevelItem);
          }
        }
        if (parentLevelItem.__level !== 0) {
          parentLevelItem = levelParentOfItem(value, parentLevelItem);
        } else {
          parentLevelItem = undefined;
        }
      }
    }
  }

  getSelectedIds(): string[] {
    if (this.bulkAllSelected) {
      return [];
    }

    // Handle aggregated ids case
    if (
      this.dataValue.schema?.columns.some(
        (column) => column.name === BULK_ACTION_AGGREGATED_IDS && column.dataType === 'Array',
      )
    ) {
      return this.selection.selected
        .flatMap((row: any) => row?.[BULK_ACTION_AGGREGATED_IDS]) // Extract all nested ids
        .filter(Boolean); // Remove empty values (used by "Group By" header rows)
    }

    const idColumn = getDataSourceIdColumn(this.propsDataSource(), this.schemaService) ?? '';
    return this.selection.selected.map((row: any) => row?.[idColumn]);
  }

  onAction({
    event,
    action,
    dataRow,
    isBulkAction,
    actionId,
    menu,
    output,
    dataRowSchema,
  }: OnContainerActionParamsWithExtras): Promise<ListLikeActionResult | undefined> | undefined {
    event?.stopPropagation();
    menu?.closed.emit();

    if (dataRow === null || (isBulkAction && !this.selectionCount)) {
      return;
    }

    const dataSource = this.propsDataSource();
    if (!dataSource) {
      console.error(`Action "${action.label}" failed. Data source not configured.`);
      return;
    }

    if (actionId) {
      this.setPendingAction(actionId, true);
    }

    const idColumnName = this.idColumnName();
    const params = isBulkAction
      ? {
          isBulkAction: true,
          dataRowIds: this.getSelectedIds(),
        }
      : {
          dataRow,
          dataRowId: idColumnName && dataRow ? dataRow[idColumnName] : undefined,
        };

    return this.containerActionsService.handleListlikeAction({
      dataSource,
      action,
      props: this.props() as RowActionsProps | BulkActionsProps,
      output,
      dataRowSchema,
      ...params,
    });
  }

  /**
   * @returns element's visible actions.
   */
  getVisibleActions<T>(
    context: Observable<RuntimeContext>,
    dataRows: Observable<T[]>,
    rowActions: Observable<PropertiesConfigInputContainerItemActionModel[] | undefined>,
    element$: Observable<CanvasElement>,
  ): Observable<RuntimeContainerActionModel[][]> {
    return element$.pipe(
      filter((element) => !!element.id),
      first(),
      switchMap((element) => {
        return combineLatest({
          blockingWorkflowIds: this.getActionBlockingWorkflowIds(element.id),
          context,
          dataSource: dataRows.pipe(distinctUntilChanged<T[]>(isDeepEqual)),
          rowActions,
          userAccessRoles: this.userAccessRolesService.userAccessRoles$,
        }).pipe(
          map(({ blockingWorkflowIds, context, dataSource, rowActions }) => {
            const blockingWfIds = new Set(blockingWorkflowIds);
            const allActions = (rowActions || []).map((rowAction) => {
              if (rowAction.action?.workflowId) {
                const isActionDisabledInAllRows = blockingWfIds.has(rowAction.action.workflowId);
                return {
                  ...rowAction,
                  isActionDisabledInAllRows,
                  showLoader: isActionDisabledInAllRows,
                };
              }
              return rowAction;
            }) as RuntimeContainerActionModel[];
            return { allActions, context, dataSource };
          }),
          map(({ context, dataSource, allActions }) => {
            const rowActions = allActions.filter(
              (a) =>
                !a.visible?.accessRoles?.length ||
                this.userAccessRolesService.isCurrentUserInAccessRoles(a.visible.accessRoles),
            );
            return { context, dataSource, rowActions };
          }),
          map(({ context, dataSource, rowActions }) => {
            const elementId = this.elementId();
            if (!elementId) {
              return [];
            }

            // If no actions are conditionally visible - show the same actions for all rows
            if (!rowActions.some((action) => !!action.visibilityExpression)) {
              const visibleActions = dataSource.map((rowData) =>
                rowActions.map((action) => ({
                  ...action,
                  actionId: this.createActionId(action, rowData, undefined, false),
                })),
              );
              return visibleActions;
            }

            // Normalization is done here for improved performance - the normalized context is shared across all rows
            const normalizedContext = this.intellisense.normalizeLinkedParams(context);
            let isAnyActionVisible = false;
            const visibleActions = dataSource.map((rowData) => {
              // Add normalized row data to the context
              const rowContext = {
                ...normalizedContext,
                row: rowData,
                output: this.intellisense.normalizeLinkedParams(rowData as RuntimeContext),
              };
              // Show row action if the visibility expression is not provided or if it resolves to true
              return (rowActions || [])
                .filter(
                  (action) =>
                    !action.visibilityExpression ||
                    !!evaluateExpression({
                      expression: action.visibilityExpression,
                      context: rowContext,
                      intellisense: this.intellisense,
                      ignoreNormalization: true,
                      elementId,
                      elementType: this.elementType,
                    }),
                )
                .map((action) => {
                  isAnyActionVisible = true;
                  return {
                    ...action,
                    actionId: this.createActionId(action, rowData, undefined, false),
                  };
                });
            });
            return isAnyActionVisible ? visibleActions : [];
          }),
          countedShareReplay(),
        );
      }),
    );
  }

  getVisibleComponentActions(
    context$: Observable<RuntimeContext>,
    elementId: CanvasElementId,
    bulkActions$: Observable<PropertiesConfigInputContainerItemActionModel[] | undefined>,
  ): Observable<RuntimeContainerActionModel[]> {
    return combineLatest({
      context: context$.pipe(startWith(null)),
      bulkActions$,
      userAccessRoles: this.userAccessRolesService.userAccessRoles$,
    }).pipe(
      map(({ context, bulkActions$: allActions }) => {
        const bulkActions =
          allActions?.filter(
            (a) =>
              !a.visible?.accessRoles?.length ||
              this.userAccessRolesService.isCurrentUserInAccessRoles(a.visible.accessRoles),
          ) ?? [];
        if (context === null) {
          // If context is not ready (null) - do not show any actions
          return [];
        }

        // Normalization is done here for improved performance - the normalized context is shared across all actions
        const normalizedContext = bulkActions.some((action) => !!action.visibilityExpression)
          ? this.intellisense.normalizeLinkedParams(context)
          : undefined;

        // Show bulk action if the visibility expression is not provided or if it resolves to true
        const visibleActions = normalizedContext
          ? bulkActions.filter(
              (action: PropertiesConfigInputContainerItemActionModel): boolean =>
                !action.visibilityExpression ||
                !!evaluateExpression({
                  expression: action.visibilityExpression,
                  context: normalizedContext,
                  intellisense: this.intellisense,
                  ignoreNormalization: true,
                  elementId,
                  elementType: this.elementType,
                }),
            )
          : bulkActions;
        const result = visibleActions.map((action) => ({
          ...action,
          actionId: this.createActionId(action, undefined, undefined, true),
        }));

        return result;
      }),
    );
  }

  clearPendingActions(): void {
    for (const actionId in this.pendingActions()) {
      this.deletePendingAction(actionId);
    }
  }

  clearActionsNoLongerPending(element$: Observable<CanvasElement>): Observable<void> {
    // Clear pending action flags when no actions are pending.
    return element$.pipe(
      filter((element) => !!element.id),
      first(),
      switchMap((element) =>
        this.store.select(runtimeSelectors.selectRowActionPendingUiByElementId(element.id)),
      ),
      map((pendingUi) => {
        const pendingIds = pendingUi.map(({ rowId, workflowId }) => {
          let pendingId = undefined;
          if (!isNullishOrEmptyString(rowId) && workflowId) {
            // row action
            pendingId = this.createActionIdFromRowAndWfId(rowId, workflowId);
          } else if (isNullishOrEmptyString(rowId) && workflowId) {
            // custom bulk actions don't have rowId associated with them
            pendingId = this.createActionIdForBulkAction(workflowId);
          }
          return pendingId;
        });
        const pendingIdsSet = new Set(pendingIds);
        for (const actionId in this.pendingActions()) {
          if (!pendingIdsSet.has(actionId)) {
            this.deletePendingAction(actionId);
          }
        }
      }),
    );
  }

  protected createActionIdForBulkAction(workflowId: string): string {
    return `bulk_${workflowId}`;
  }

  protected createActionIdFromRowAndWfId(rowId: string, workflowId: string): string {
    return `${rowId}_${workflowId}`;
  }

  protected setPendingAction(actionId: string, isPending: boolean): void {
    if (!actionId) {
      return;
    }

    if (isPending) {
      this.pendingActions.set({ ...this.pendingActions(), [actionId]: Date.now() });
    } else {
      this.deletePendingAction(actionId);
    }
  }

  private createActionId(
    action: RuntimeContainerActionModel,
    dataRow?: any,
    idColumnName?: string,
    isBulkAction?: boolean,
  ): string | undefined {
    // Bulk / header-level custom actions share the workflowId-based pending key
    if (action.action?.workflowId && (isBulkAction || !dataRow)) {
      return this.createActionIdForBulkAction(action.action.workflowId);
    } else if (isBulkAction && !action.action?.workflowId) {
      // TODO: Generic bulk actions (edit, delete, export) are not yet supported.
      return undefined;
    }

    // Row action
    idColumnName = idColumnName || this.idColumnName() || '';
    if (!idColumnName) {
      return undefined;
    }
    const rowId = dataRow[idColumnName];
    const workflowId = action?.action?.workflowId;
    if (isNullishOrEmptyString(rowId) || !workflowId) {
      return;
    }
    return this.createActionIdFromRowAndWfId(rowId, workflowId);
  }

  /**
   * @returns IDs of workflows which should be blocked if one of those workflows is being executed.
   */
  private getActionBlockingWorkflowIds(elementId: string): Observable<string[]> {
    return this.store.select(runtimeSelectors.selectRowActionPendingUiByElementId(elementId)).pipe(
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectWorkflows)),
      map(([pendingUi, workflows]) => {
        const blockingWfIds = pendingUi
          .filter(({ workflowId }) => {
            const wf = workflows.find(({ id }) => id === workflowId);
            return wf?.nodes?.find((node) => isPrintToPdfNode(node));
          })
          .map(({ workflowId }) => workflowId);

        return blockingWfIds;
      }),
      distinctUntilChanged(isDeepEqual),
    );
  }

  private deletePendingAction(actionId: string): void {
    const timeSinceActionStarted = Date.now() - this.pendingActions()[actionId];
    let timeLeftToEnableAction = DISABLE_ACTION_WHEN_PENDING_TIME - timeSinceActionStarted;
    timeLeftToEnableAction = timeLeftToEnableAction < 0 ? 0 : timeLeftToEnableAction;
    setTimeout(() => {
      const actions = { ...this.pendingActions() };
      delete actions[actionId];
      this.pendingActions.set(actions);
    }, timeLeftToEnableAction);
  }
}

export function getDataSourceIdColumn(
  dataSource: DataBindingSourceInfo | undefined,
  schemaService: SchemaService,
): string | undefined {
  if (!dataSource) {
    return undefined;
  }

  const idColumn = schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType) || '';
  return idColumn;
}
